import React from 'react';
import { MessageCircle, BarChart3, Users, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import CorporateWellness from '../components/CorporateWellness';
import Contact from '../components/Contact';

const CorporateWellnessPage = () => {
  const whatsappNumber = "+919088771406";

  const benefits = [
    {
      title: "Reduced Burnout & Absenteeism",
      description: "Targeted energy cleansing and stress management practices significantly lower workplace fatigue and mental strain.",
      icon: RefreshCw
    },
    {
      title: "Enhanced Team Morale & Cohesion",
      description: "Group healing and mass energy work foster alignment, mutual empathy, and positive workplace culture.",
      icon: Users
    },
    {
      title: "Measurable ROI & Feedback Reports",
      description: "Our 6-Month Customized Program includes monthly engagement feedback and qualitative wellbeing tracking.",
      icon: BarChart3
    }
  ];

  return (
    <div className="min-h-screen bg-background">

      {/* Main Corporate Offerings Component */}
      <CorporateWellness />

      {/* Organizational Value Props */}
      <section className="bg-white border-t border-lotus-petal px-6 py-16 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-lotus-deep block mb-2">
              Why Corporate Wellness Matters
            </span>
            <h3 className="font-serif text-3xl sm:text-4xl text-lotus-ink">
              Transforming Workplaces from Within
            </h3>
            <p className="mt-3 text-base text-lotus-ink/70">
              When employee energy is aligned and stress is proactively managed, productivity, creativity, and retention naturally soar.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {benefits.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                  whileHover={{ y: -6 }}
                  className="rounded-3xl bg-background p-8 border border-lotus-petal/70 flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:border-lotus-deep/40"
                >
                  <div>
                    <div className="size-12 rounded-2xl bg-lotus-petal/60 flex items-center justify-center text-lotus-deep mb-6">
                      <Icon size={24} />
                    </div>
                    <h4 className="font-serif text-2xl font-medium text-lotus-ink mb-3">
                      {item.title}
                    </h4>
                    <p className="text-sm sm:text-base text-lotus-ink/75 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* WhatsApp Direct Proposal Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 rounded-3xl bg-lotus-deep p-8 text-white text-center sm:p-12 shadow-xl"
          >
            <h4 className="font-serif text-3xl font-medium mb-3">Ready to Empower Your Workforce?</h4>
            <p className="text-sm sm:text-base text-white/90 max-w-2xl mx-auto mb-8">
              Request a custom proposal today. We will tailor the program schedule, module duration, and pricing based on your headcount and needs.
            </p>
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={`https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent("Hi Lotus Prana, I would like to request a customized Corporate Wellness proposal for our team.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-xs font-bold uppercase tracking-widest text-lotus-ink hover:bg-lotus-petal transition-all shadow-lg active:scale-95"
            >
              <MessageCircle size={18} className="text-lotus-deep" />
              <span>Request Corporate Proposal via WhatsApp</span>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Footer Contact */}
      <Contact />
    </div>
  );
};

export default CorporateWellnessPage;
